// import hooks
import { MouseEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// import components
import { Button, IconButton, Popover, Typography } from '@mui/material';
import { ChromePicker } from 'react-color';

// import styles
import {
  ColorSquare,
  StyledColorPickerBox,
  StyledColorPickerGrid,
  SvgIcon
} from './ColorPickerPopover.styles';

// import types
import { ColorPickerPopoverProps, HexColor } from './ColorPickerPopover.types';

// import utils
import { isNil } from 'ramda';
import { GridContainerAlignCenter } from '../Grid/Grid.components';
import { StyledBoxFlexRow } from '../Box/Box.styles';

const ColorPickerPopover = ({
  color = '',
  label,
  onColorSelected,
  hasEditFeature
}: ColorPickerPopoverProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [value, setValue] = useState<HexColor>(color);
  const [isHovering, setIsHovering] = useState<boolean>(false);
  const { t } = useTranslation();

  const isOpen = Boolean(anchorEl);
  const id = isOpen ? 'color-picker' : undefined;

  useEffect(() => {
    setValue(color);
  }, [color]);

  const onClickOpen = (evt: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(evt.currentTarget);
    setIsHovering(false);
  };

  const onClickSave = () => {
    onColorSelected(value);
    setAnchorEl(null);
    setIsHovering(false);
  };

  const onClickCancel = () => {
    setValue(color);
    setAnchorEl(null);
    setIsHovering(false);
  };

  const onChangeColor = ({ hex }) => {
    setValue(hex);
  };

  const onIconButtonMouseOver = () => {
    if (hasEditFeature) {
      setIsHovering(true);
    }
  };

  const onIconButtonMouseOut = () => {
    if (hasEditFeature) {
      setIsHovering(false);
    }
  };

  return (
    <GridContainerAlignCenter>
      <StyledColorPickerGrid>
        <IconButton
          onMouseOver={onIconButtonMouseOver}
          onMouseOut={onIconButtonMouseOut}
          onClick={onClickOpen}
          size="small"
        >
          {isHovering ? <SvgIcon type="pen" /> : <ColorSquare color={value} />}
        </IconButton>
        {!isNil(label) && <Typography>{label}</Typography>}
      </StyledColorPickerGrid>
      <Popover
        id={id}
        open={isOpen}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
      >
        <StyledColorPickerBox>
          <ChromePicker
            disableAlpha={true}
            color={value}
            onChangeComplete={onChangeColor}
          />
          <StyledBoxFlexRow>
            <Button variant="outlined" onClick={onClickCancel}>
              {t('cancel')}
            </Button>
            <Button variant="contained" onClick={onClickSave}>
              {t('save')}
            </Button>
          </StyledBoxFlexRow>
        </StyledColorPickerBox>
      </Popover>
    </GridContainerAlignCenter>
  );
};

export default ColorPickerPopover;
